import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Point, SimpleGeometry } from "ol/geom";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Feature, Map } from "ol";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { config } from "../config";
import { coordinatesConverter, EpsgCode } from "@geo/js";
import { unByKey } from "ol/Observable";
import { EventsKey } from "ol/events";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import i18next from "i18next";
import "./GeoLocation.scss";

enum LocationState {
    Inactive = 0,
    Waiting = 1,
    Watching = 2,
    Tracking = 3
}

export interface IPosition {
    coords: {
        latitude: number;
        longitude: number;
    };
}

const centerGraphicStyle = new Style({
    image: new CircleStyle({
        radius: 6,
        fill: new Fill({
            color: "#fff"
        }),
        stroke: new Stroke({
            color: "#00aeea",
            width: 4
        })
    })
});

const outlineGraphicStyle = new Style({
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#00aeea4d",
            width: 4
        })
    })
});
export const GeoLocationButton = ({ map }: { map: Map }): JSX.Element => {
    const [locationState, setLocationState] = useState(LocationState.Inactive);
    const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
    const [isMapMoving, setIsMapMoving] = useState(false);
    const gpsWatcher = useRef<number>();
    const interactingWatcher = useRef<any>();
    const stationaryWatcher = useRef<EventsKey>();
    const centerGraphic = useRef<Feature>();
    const outlineGraphic = useRef<Feature>();
    const pointLayer = useRef<VectorLayer<VectorSource>>();

    const geolocationOptions = config
        ? config.geoLocation.geolocationOptions
        : {
              maximumAge: 0,
              timeout: 15000,
              enableHighAccuracy: true
          };

    useEffect(() => {
        if (locationState === LocationState.Waiting) {
            startLocate();
        } else if (locationState === LocationState.Tracking) {
            startTracking();
        } else if (locationState === LocationState.Inactive) {
            stopLocate();
        }
    }, [locationState]);

    useEffect(() => {
        if (position.latitude === 0 && position.longitude === 0) {
            return;
        }

        placeMarker();
        if (locationState === LocationState.Waiting && !isMapMoving) {
            setIsMapMoving(true);
            mapGoTo(undefined, true);
        } else if (locationState === LocationState.Tracking && !isMapMoving) {
            setIsMapMoving(true);
            mapGoTo(map.getView().getZoom());
        }
    }, [position]);

    const onSuccess = (position: IPosition) => {
        const point = coordinatesConverter.convert(
            [position.coords.longitude, position.coords.latitude],
            EpsgCode.Wgs84,
            EpsgCode.Lks92
        );
        setPosition({ latitude: point[1], longitude: point[0] });
    };

    const onError = () => {
        setLocationState(LocationState.Inactive);
    };

    const startLocate = () => {
        gpsWatcher.current = navigator.geolocation.watchPosition(onSuccess, onError, geolocationOptions);
    };

    const stopLocate = () => {
        gpsWatcher.current && navigator.geolocation.clearWatch(gpsWatcher.current);

        stopTracking();

        if (pointLayer.current) {
            pointLayer.current.getSource().clear();
            //@ts-ignore
            map.removeLayer(pointLayer.current);
            pointLayer.current = null;
            centerGraphic.current = null;
            outlineGraphic.current = null;
        }
    };

    const startTracking = () => {
        //@ts-ignore
        const view = map.getView();

        if (!interactingWatcher.current) {
            interactingWatcher.current = setInterval(() => {
                if (view.getInteracting() && locationState != LocationState.Watching) {
                    setLocationState(LocationState.Watching);
                }
            }, 150);
        }
        if (!stationaryWatcher.current) {
            //@ts-ignore
            stationaryWatcher.current = map.on("moveend", refreshPosition);
        }

        if (!view.getInteracting() && !view.getAnimating()) {
            refreshPosition();
        }

        document.addEventListener("stopTracking", () => {
            stopTracking();
            setLocationState(LocationState.Watching);
        });
    };

    const stopTracking = () => {
        if (stationaryWatcher.current) {
            unByKey(stationaryWatcher.current);
            stationaryWatcher.current = null;
        }
        if (interactingWatcher.current) {
            clearInterval(interactingWatcher.current);
            interactingWatcher.current = null;
        }
        document.removeEventListener("stopTracking", stopTracking);
    };

    const refreshPosition = () => {
        if (locationState === LocationState.Tracking) {
            setPosition({
                latitude: position.latitude,
                longitude: position.longitude
            });
        }
    };

    const placeMarker = () => {
        const centerPoint = new Point([position.latitude, position.longitude]);

        centerGraphic.current = new Feature();
        centerGraphic.current.setStyle(centerGraphicStyle);
        centerGraphic.current.setGeometry(centerPoint);

        outlineGraphic.current = new Feature();
        outlineGraphic.current.setStyle(outlineGraphicStyle);
        outlineGraphic.current.setGeometry(centerPoint);

        if (!pointLayer.current) {
            pointLayer.current = new VectorLayer({
                //@ts-ignore
                map: map,
                source: new VectorSource({
                    features: [centerGraphic.current, outlineGraphic.current]
                })
            });
        }
    };

    const mapGoTo = async (maxZoom: number, enableTracking?: boolean) =>
        map.getView().fit(centerGraphic.current.getGeometry() as SimpleGeometry, {
            duration: 1000,
            maxZoom,
            callback: () => {
                setIsMapMoving(false);
                enableTracking && setLocationState(LocationState.Tracking);
            }
        });

    const onClick = () => {
        let nextState = locationState;
        switch (locationState) {
            case LocationState.Inactive:
                nextState = LocationState.Waiting;
                break;
            case LocationState.Watching:
                nextState = LocationState.Tracking;
                break;
            default:
                nextState = LocationState.Inactive;
                break;
        }
        setLocationState(nextState);
    };

    const buttonClassName =
        locationState === LocationState.Inactive
            ? "gps-inactive"
            : locationState === LocationState.Waiting
            ? "gps-waiting"
            : locationState === LocationState.Tracking
            ? "gps-tracking"
            : "gps-watching";

    const iconClassName = locationState === LocationState.Waiting ? "gps-spinner" : "icon-geolite-gps";

    return (
        <div className="geolite-map-button location-container ol-unselectable ol-control">
            <ToolbarButton
                className={buttonClassName}
                iconClassName={iconClassName}
                onClick={onClick}
                title={i18next.t("geolocation.findMyLocation")}
            ></ToolbarButton>
        </div>
    );
};
